import React, { useState } from 'react';
import { Box, Snackbar, Alert, Typography } from '@mui/material';
import { COLORS_ADMIN_PAGE, ResizeHandle } from '../conf/constants';
import { useErrorHandler } from '../utils/useErrorHandler';
import { downloadFile } from './utils/fileUtils';

import { useContractors } from './hooks/useContractors';
import { useDocuments } from './hooks/useDocuments';
import { useAddresses } from './hooks/useAddresses';
import { useResizableSidebar } from './hooks/useResizableSidebar';

import ContractorSidebar from './components/ContractorSidebar';
import ContractorDetails from './components/ContractorDetails';
import DocumentStatusModal from './components/DocumentStatusModal';
import { CONTRACTOR_MANAGEMENT_CONSTANTS } from './constants/contractorSidebar';

const ContractorManagement: React.FC = () => {
  const { snackbar, handleErrorWithLogout, showSuccessMessage, handleSnackbarClose } = useErrorHandler();
  
  const [openModal, setOpenModal] = useState(false);
  const [modalStatus, setModalStatus] = useState<number | null>(null);

  const {
    contractors,
    selectedContractor,
    searchTerm,
    currentPage,
    totalContractors,
    contractorsPerPage,
    setSearchTerm,
    handleContractorSelect,
    handlePageChange,
    onContractorsPerPageChange, 
  } = useContractors({ handleErrorWithLogout });

  const {
    documents,
    selectedDocumentId,
    selectedDocument,
    setSelectedDocumentId,
    handleDocumentStatusChange,
    handleVerifyUserChange
  } = useDocuments({ 
    userId: selectedContractor?.userId || null,
    handleErrorWithLogout,
    showSuccessMessage
  });

  const { addresses } = useAddresses({ 
    userId: selectedContractor?.userId || null,
    handleErrorWithLogout
  });

  const {
    sidebarWidth,
    containerRef,
    isResizing,
    handleMouseDown,
  } = useResizableSidebar();

  const handleDocumentAction = async (status: number) => {
    if (!selectedContractor || !selectedDocument) return;

    const { DOCUMENT_STATUS, MESSAGES } = CONTRACTOR_MANAGEMENT_CONSTANTS;

    if (status === DOCUMENT_STATUS.APPROVED) { 
      await handleDocumentStatusChange(
        selectedContractor.userId,
        selectedDocument.documentId,
        status,
        'approve'
      );
      showSuccessMessage(MESSAGES.DOCUMENT_UPDATED_SUCCESS);
    } else if (status === DOCUMENT_STATUS.PENDING || status === DOCUMENT_STATUS.REJECTED) { 
      setModalStatus(status);
      setOpenModal(true);
    }
  };

  const handleVerifyContract = async () => {
    if (!selectedContractor) return;
    try {
      await handleVerifyUserChange(selectedContractor.userId);
      showSuccessMessage(CONTRACTOR_MANAGEMENT_CONSTANTS.MESSAGES.CONTRACTOR_VERIFIED_SUCCESS);
    } catch (error) {
      handleErrorWithLogout(error);
    }
  };

  const handleModalSubmit = async (notes: string) => {
    if (selectedContractor && selectedDocument && modalStatus) {
      await handleDocumentStatusChange(
        selectedContractor.userId,
        selectedDocument.documentId,
        modalStatus,
        notes
      );
      setOpenModal(false);
      setModalStatus(null);
      showSuccessMessage(CONTRACTOR_MANAGEMENT_CONSTANTS.MESSAGES.DOCUMENT_STATUS_UPDATED_SUCCESS);
    }
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setModalStatus(null);
  };

  const handleDownloadDocument = () => {
    if (selectedDocument) {
      downloadFile(selectedDocument.filename);
    }
  };

  return (
    <Box
      sx={{ 
        display: 'flex', 
        bgcolor: COLORS_ADMIN_PAGE.BACKGROUND, 
        height: CONTRACTOR_MANAGEMENT_CONSTANTS.CONTAINER_HEIGHT 
      }}
      ref={containerRef}
      style={{ cursor: isResizing ? 'col-resize' : 'auto' }}
    >
      {/* Sidebar */}
      <ContractorSidebar
        contractors={contractors}
        selectedContractor={selectedContractor}
        searchTerm={searchTerm}
        currentPage={currentPage}
        totalContractors={totalContractors}
        contractorsPerPage={contractorsPerPage}
        sidebarWidth={sidebarWidth}
        onContractorSelect={handleContractorSelect}
        onSearchChange={setSearchTerm}
        onPageChange={handlePageChange}
        onContractorsPerPageChange={onContractorsPerPageChange} 
      />

      {/* Resize Handle */}
      <ResizeHandle onMouseDown={handleMouseDown} />

      {/* Main Content */}
      <Box sx={{ flex: 1, bgcolor: COLORS_ADMIN_PAGE.WHITE, flexShrink: 0 }}>
        {selectedContractor ? (
          <ContractorDetails
            contractor={selectedContractor}
            addresses={addresses}
            documents={documents}
            selectedDocumentId={selectedDocumentId}
            onDocumentSelect={setSelectedDocumentId}
            onDocumentAction={handleDocumentAction}
            onVerifyContract={handleVerifyContract}
            onDownloadDocument={handleDownloadDocument}
          />
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6">
              {CONTRACTOR_MANAGEMENT_CONSTANTS.MESSAGES.NO_CONTRACTOR_SELECTED}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={CONTRACTOR_MANAGEMENT_CONSTANTS.SNACKBAR_DURATION}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Status Modal */}
      <DocumentStatusModal
        open={openModal}
        status={modalStatus}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
      />
    </Box>
  );
};

export default ContractorManagement;